"use client";

import { useState } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { httpBatchLink, httpBatchStreamLink, httpLink, loggerLink, splitLink } from "@trpc/client";
import { createTRPCReact } from "@trpc/react-query";
import { type inferRouterInputs, type inferRouterOutputs } from "@trpc/server";
import { ErrorBoundary } from "react-error-boundary";
import SuperJSON from "superjson";

import { type AppRouter } from "~/server/api/root";

const createQueryClient = () =>
  new QueryClient({
    defaultOptions: {
      queries: {
        queryKeyHashFn: (queryKey) => {
          return JSON.stringify(SuperJSON.serialize(queryKey));
        },
      },
    },
  });

let clientQueryClientSingleton: QueryClient | undefined = undefined;
const getQueryClient = () => {
  if (typeof window === "undefined") {
    // Server: always make a new query client
    return createQueryClient();
  }
  // Browser: use singleton pattern to keep the same query client
  return (clientQueryClientSingleton ??= createQueryClient());
};

export const api = createTRPCReact<AppRouter>();
/**
 * Inference helper for inputs.
 *
 * @example type HelloInput = RouterInputs['example']['hello']
 */
export type RouterInputs = inferRouterInputs<AppRouter>;

/**
 * Inference helper for outputs.
 *
 * @example type HelloOutput = RouterOutputs['example']['hello']
 */
export type RouterOutputs = inferRouterOutputs<AppRouter>;

const customHeader = () => {
  const headers = new Headers();
  headers.set("x-trpc-source", "nextjs-react");
  return headers;
};

export function TRPCReactProvider(props: { children: React.ReactNode }) {
  const queryClient = getQueryClient();

  const [trpcClient] = useState(() =>
    api.createClient({
      links: [
        loggerLink({
          enabled: (op) =>
            process.env.NODE_ENV === "development" || (op.direction === "down" && op.result instanceof Error),
        }),
        splitLink({
          condition(op) {
            const ctx = op.context as { skipBatch?: boolean; skipStreaming?: boolean };
            return ctx.skipBatch ?? false;
          },
          // when condition is true, use normal request
          true: httpLink({
            transformer: SuperJSON,
            url: getBaseUrl() + "/api/trpc",
            headers: customHeader,
          }),
          // when condition is false, use batching
          false: splitLink({
            condition(op) {
              const ctx = op.context as { skipBatch?: boolean; skipStreaming?: boolean };
              return ctx.skipStreaming ?? false;
            },
            true: httpBatchLink({
              transformer: SuperJSON,
              url: getBaseUrl() + "/api/trpc",
              headers: customHeader,
            }),
            false: httpBatchStreamLink({
              transformer: SuperJSON,
              url: getBaseUrl() + "/api/trpc",
              headers: customHeader,
            }),
          }),
        }),
      ],
    }),
  );

  return (
    <api.Provider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>
        <ErrorBoundary FallbackComponent={ErrorScreen}>{props.children}</ErrorBoundary>
      </QueryClientProvider>
    </api.Provider>
  );
}

function ErrorScreen({ error }: { error: Error }) {
  return (
    <div style={{ textAlign: "center", marginTop: "50px" }}>
      <h1>Oops! Something went wrong</h1>
      <p>We're having trouble processing your request. Please try again later.</p>
      {process.env.NODE_ENV === "development" && (
        <pre style={{ textAlign: "left", margin: "20px", padding: "10px", background: "#f0f0f0" }}>{error.message}</pre>
      )}
    </div>
  );
}

function getBaseUrl() {
  if (typeof window !== "undefined") return window.location.origin;
  return `http://localhost:${process.env.PORT ?? 3000}`;
}
